import { TrendingUp, TrendingDown, Clock } from 'lucide-react'

interface TrendBadgeProps {
  hasHistoricalData: boolean
  trend: 'up' | 'down' | 'stable'
  value: number
  unit: string  // '%', ' pts', ' pos', ''
  showPlusSign?: boolean  // For positive values
  decimals?: number  // 0, 1, 2
  onClick: () => void
}

export default function TrendBadge({
  hasHistoricalData,
  trend,
  value,
  unit,
  showPlusSign = false,
  decimals = 1,
  onClick
}: TrendBadgeProps) {
  // Show "Tracking" badge when no historical data
  if (!hasHistoricalData) {
    return (
      <div
        className="flex items-center space-x-1 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500 border border-gray-200"
        title="Trend data available after 30 days of tracking"
      >
        <Clock className="h-3 w-3" />
        <span>Tracking</span>
      </div>
    )
  }

  // Format the value based on trend state
  // Show "no change" for stable trend instead of "0.0 pos" or "0.0%"
  const formattedValue = trend === 'stable'
    ? 'no change'
    : `${showPlusSign && value > 0 ? '+' : ''}${decimals === 0 ? Math.round(value) : value.toFixed(decimals)}${unit}`

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 transition-all ${
        trend === 'up' ? 'bg-green-100 text-green-700 hover:ring-green-300' :
        trend === 'down' ? 'bg-red-100 text-red-700 hover:ring-red-300' :
        'bg-blue-100 text-blue-700 hover:ring-blue-300'
      }`}
    >
      {trend === 'up' ? (
        <TrendingUp className="h-3 w-3" />
      ) : trend === 'down' ? (
        <TrendingDown className="h-3 w-3" />
      ) : (
        <div className="h-3 w-3 flex items-center justify-center">
          <div className="w-2 h-0.5 bg-blue-700 rounded"></div>
        </div>
      )}
      <span>{formattedValue}</span>
    </div>
  )
}
